import {
	qsRequired,
	qsaOptional,
	targetRequired,
} from '@/scripts/core/global'
import { removeTrapFocus, trapFocus } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class ArticleVideoModal extends UcoastEl {
	static htmlSelector = 'article-video-modal'
	modal: HTMLElement
	overlay: HTMLElement
	closeButton: HTMLButtonElement
	videoContainer: HTMLElement
	videoShadow: HTMLElement
	loadingSpinner: HTMLElement
	onBodyClickEvent?: (event: MouseEvent) => void
	onEscapeKeyEvent?: (event: KeyboardEvent) => void

	content: HTMLElement

	constructor() {
		super()
		this.modal = qsRequired('.article-video-modal__modal', this)
		this.overlay = qsRequired('.article-video-modal__overlay', this)
		this.closeButton = qsRequired('.article-video-modal__close', this)
		this.videoContainer = qsRequired('.article-video-modal__video-container', this)
		this.content = qsRequired('.article-video-modal__content', this)
		this.videoShadow = qsRequired('.article-video-modal__video-shadow', this)
		this.loadingSpinner = qsRequired('.article-video-modal__loading', this)

		this.closeButton.addEventListener('click', this.close.bind(this))
		this.overlay.addEventListener('click', this.close.bind(this))

		// Find and attach click handlers to video buttons
		this.attachVideoButtons()
	}

	attachVideoButtons() {
		const videoButtons = qsaOptional<HTMLButtonElement>('[data-video-html]')
		if (videoButtons) {
			videoButtons.forEach((button) => {
				button.addEventListener('click', (event) => {
					event.preventDefault()
					event.stopPropagation()
					const videoHtml = button.getAttribute('data-video-html')
					if (videoHtml) {
						this.open(videoHtml)
					}
				})
			})
		}
	}

	open(videoHtml: string) {
		// Show loading spinner and shadow
		if (this.loadingSpinner) {
			this.loadingSpinner.classList.remove('hidden')
			this.loadingSpinner.style.display = 'flex'
		}
		this.videoShadow.style.display = 'block'
		
		// Decode HTML entities if needed
		const decodedHtml = this.decodeHtmlEntities(videoHtml)
		
		// Extract dimensions before inserting HTML
		const widthMatch = decodedHtml.match(/width=["'](\d+)["']/i)
		const heightMatch = decodedHtml.match(/height=["'](\d+)["']/i)
		
		let width: number | null = null
		let height: number | null = null
		
		if (widthMatch && heightMatch) {
			width = parseInt(widthMatch[1])
			height = parseInt(heightMatch[1])
		}
		
		// Set shadow dimensions to match video
		if (width && height && width > 0 && height > 0) {
			if (width > height) {
				// Horizontal video
				this.videoShadow.style.width = '100%'
				this.videoShadow.style.aspectRatio = `${width} / ${height}`
				this.videoShadow.style.height = 'auto'
			} else {
				// Vertical video
				this.videoShadow.style.width = `${width}px`
				this.videoShadow.style.height = `${height}px`
				this.videoShadow.style.maxWidth = '100%'
			}
		} else {
			// Default to 16:9
			this.videoShadow.style.width = '100%'
			this.videoShadow.style.aspectRatio = '16 / 9'
			this.videoShadow.style.height = 'auto'
		}
		
		// Insert the video HTML into the container
		const tempDiv = document.createElement('div')
		tempDiv.innerHTML = decodedHtml
		const iframe = tempDiv.querySelector('iframe')
		
		if (iframe) {
			// Clear container but keep the loading spinner
			const loadingElement = this.loadingSpinner
			this.videoContainer.innerHTML = ''
			// Re-add the loading spinner
			this.videoContainer.appendChild(loadingElement)
			// Add iframe
			this.videoContainer.appendChild(iframe)
			
			// Set iframe dimensions
			if (width && height && width > 0 && height > 0) {
				if (width > height) {
					iframe.style.width = '100%'
					iframe.style.maxWidth = '100%'
					iframe.style.aspectRatio = `${width} / ${height}`
					iframe.style.height = 'auto'
				} else {
					iframe.style.width = width + 'px'
					iframe.style.height = height + 'px'
					this.content.style.width = 'fit-content'
					this.content.style.maxWidth = '100%'
				}
			} else {
				iframe.style.width = '100%'
				iframe.style.aspectRatio = '16 / 9'
				iframe.style.height = 'auto'
			}
			
			// Hide spinner when iframe loads
			iframe.addEventListener('load', () => {
				if (this.loadingSpinner) {
					this.loadingSpinner.classList.add('hidden')
					this.loadingSpinner.style.display = 'none'
				}
				this.videoShadow.style.display = 'none'
			})
		} else {
			// If no iframe found, hide spinner anyway
			if (this.loadingSpinner) {
				this.loadingSpinner.classList.add('hidden')
				this.loadingSpinner.style.display = 'none'
			}
			this.videoShadow.style.display = 'none'
		}

		// Show the modal
		this.modal.classList.add('article-video-modal__modal--open')
		document.body.style.overflow = 'hidden'

		// Set up event listeners
		const bodyClickHandler = this.onBodyClick.bind(this)
		const escapeKeyHandler = this.onEscapeKey.bind(this)
		
		this.onBodyClickEvent = bodyClickHandler
		this.onEscapeKeyEvent = escapeKeyHandler

		document.body.addEventListener('click', bodyClickHandler)
		document.addEventListener('keydown', escapeKeyHandler)

		// Trap focus within the modal
		trapFocus(
			this.closeButton,
			// iframe || undefined
		)
	}

	close() {
		// Hide the modal
		this.modal.classList.remove('article-video-modal__modal--open')
		document.body.style.overflow = ''

		// Remove event listeners
		if (this.onBodyClickEvent) {
			document.body.removeEventListener('click', this.onBodyClickEvent)
			this.onBodyClickEvent = undefined
		}
		if (this.onEscapeKeyEvent) {
			document.removeEventListener('keydown', this.onEscapeKeyEvent)
			this.onEscapeKeyEvent = undefined
		}

		// Clear the video container
		this.videoContainer.innerHTML = ''
		
		// Reset shadow and show spinner again
		this.videoShadow.style.display = 'none'
		if (this.loadingSpinner) {
			this.loadingSpinner.classList.remove('hidden')
			this.loadingSpinner.style.display = 'flex'
		}

		// Remove focus trap
		removeTrapFocus()
	}

	onBodyClick(event: MouseEvent) {
		const target = targetRequired(event)
		
		// Close if clicking on overlay
		if (target === this.overlay) {
			this.close()
			return
		}
		
		// Close if clicking outside the modal content
		// Check if the click is outside the content area but inside the modal
		if (this.modal.contains(target) && !this.content.contains(target) && target !== this.closeButton) {
			this.close()
			return
		}
		
		// Close if clicking completely outside the modal
		if (!this.modal.contains(target) && !this.contains(target)) {
			this.close()
		}
	}

	onEscapeKey(event: KeyboardEvent) {
		if (event.key === 'Escape' || event.code === 'Escape') {
			this.close()
		}
	}

	isOpen(): boolean {
		return this.modal.classList.contains('article-video-modal__modal--open')
	}

	decodeHtmlEntities(html: string): string {
		const textarea = document.createElement('textarea')
		textarea.innerHTML = html
		return textarea.value
	}
}

